import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from './WorkspaceContext';
import { isTokenExpired } from './utils/jwt';
import api from './api';

const WorkspaceJoin = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromOAuth = searchParams.get('from') === 'oauth' || new URLSearchParams(window.location.search).get('from') === 'oauth';
    const { joinWorkspace } = useWorkspace();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [role, setRole] = useState('editor');
    const [status, setStatus] = useState('Checking authentication...');
    const hasAttemptedJoin = useRef(false);

    useEffect(() => {
        const handleJoinWorkspace = async () => {
            // Prevent multiple attempts
            if (hasAttemptedJoin.current) {
                console.log('Join already attempted, skipping...');
                return;
            }
            
            if (!token) {
                setError('Invalid invite link');
                setLoading(false);
                return;
            }

            // Check if user is authenticated
            const authToken = localStorage.getItem('token');
            const isAuthenticated = authToken && !isTokenExpired(authToken);

            if (!isAuthenticated) {
                // User is not logged in - redirect to invite onboarding (no skip, sign in at end)
                console.log('User not authenticated, redirecting to invite onboarding');
                setStatus('Redirecting...');
                setTimeout(() => {
                    navigate(`/invite-onboarding/${token}`);
                }, 500);
                return;
            }

            try {
                hasAttemptedJoin.current = true;
                setLoading(true);
                setError(null);
                setStatus('Joining workspace...');

                // Join the workspace with the selected role
                const workspace = await joinWorkspace(token, role);
                
                console.log('Successfully joined workspace:', workspace);
                setStatus('Successfully joined workspace! Redirecting...');
                
                // If came from OAuth (signed in at end of invite flow), mark onboarding complete
                if (fromOAuth) {
                    try {
                        await api.completeOnboarding();
                    } catch (err) {
                        console.error('Error marking onboarding complete:', err);
                    }
                }
                // Already-signed-in users go straight to workspace; OAuth users also go to workspace (tutorial is optional via invite-onboarding)
                const target = '/workspace';
                setTimeout(() => {
                    navigate(target);
                }, fromOAuth ? 500 : 800);
                
            } catch (err) {
                console.error('Error joining workspace:', err);
                setError(err.response?.data?.error || 'Failed to join workspace');
                setStatus('Failed to join workspace');
            } finally {
                setLoading(false);
            }
        };

        handleJoinWorkspace();
    }, [token, role, joinWorkspace, navigate, fromOAuth]);

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column',
                gap: '20px',
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa'
            }}>
                <div style={{ 
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px',
                    width: '100%'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>
                        🚀
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '16px' }}>
                        Joining Workspace
                    </div>
                    <div style={{ fontSize: '18px', color: '#6c757d', marginBottom: '30px' }}>
                        {status}
                    </div>
                    
                    <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 30px'
                    }}></div>
                    
                    <div style={{ fontSize: '14px', color: '#6c757d', lineHeight: '1.5' }}>
                        {status.includes('Successfully') ? 
                            '🎉 You have been added to the workspace! You will be redirected to your workspace dashboard shortly.' :
                            status.includes('Redirecting') ?
                            '🔐 You need to log in first. You will be redirected to the login page, and then automatically joined to the workspace.' :
                            '⏳ Please wait while we process your workspace invite...'
                        }
                    </div>
                </div>
                
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        // Determine user-friendly error message based on the error
        const getErrorMessage = (error) => {
            if (error.includes('Invalid or expired invite token')) {
                return {
                    title: 'Invite Link Expired',
                    message: 'This workspace invite link has expired or is no longer valid. Please ask the workspace admin to send you a new invite link.',
                    icon: '⏰'
                };
            } else if (error.includes('already a member')) {
                return {
                    title: 'Already a Member',
                    message: 'You are already a member of this workspace. You can access it from your workspace dashboard.',
                    icon: '✅'
                };
            } else if (error.includes('Failed to join workspace')) {
                return {
                    title: 'Unable to Join Workspace',
                    message: 'There was a technical issue joining the workspace. Please try again or contact support if the problem persists.',
                    icon: '⚠️'
                };
            } else {
                return {
                    title: 'Something Went Wrong',
                    message: 'We encountered an issue processing your workspace invite. Please try again or contact support.',
                    icon: '❌'
                };
            }
        };

        const errorInfo = getErrorMessage(error);

        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column',
                gap: '20px',
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa'
            }}>
                <div style={{ 
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px',
                    width: '100%'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>
                        {errorInfo.icon}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '16px' }}>
                        {errorInfo.title}
                    </div>
                    <div style={{ fontSize: '16px', color: '#6c757d', lineHeight: '1.5', marginBottom: '30px' }}>
                        {errorInfo.message}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button 
                            onClick={() => navigate('/workspace')}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#3498db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
                        >
                            Go to My Workspace
                        </button>
                        
                        <button 
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                        >
                            Try Again
                        </button>
                    </div>
                    
                    <div style={{ 
                        marginTop: '30px', 
                        padding: '16px', 
                        backgroundColor: '#e9ecef', 
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#6c757d'
                    }}>
                        <strong>Need help?</strong> Contact support at{' '}
                        <a href="mailto:hello@merra.ai" style={{ color: '#3498db', textDecoration: 'none' }}>
                            hello@merra.ai
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return null; // This shouldn't render as we redirect on success
};

export default WorkspaceJoin;
